/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ChartDataSectionType } from 'app/constants';
import {
  ChartConfig,
  ChartDataSectionField,
  ChartStyleConfig,
  LabelStyle,
  LegendStyle,
  LineStyle,
} from 'app/types/ChartConfig';
import ChartDataSetDTO, { IChartDataSet } from 'app/types/ChartDataSet';
import {
  getAxisLabel,
  getAxisLine,
  getAxisTick,
  getColumnRenderName,
  getExtraSeriesDataFormat,
  getExtraSeriesRowData,
  getGridStyle,
  getReference2,
  getSeriesTooltips4Polar2,
  getSplitLine,
  getStyles,
  setOptionsByAxisLabelOverflow,
  toFormattedValue,
  transformToDataSet,
} from 'app/utils/chartHelper';
import { init } from 'echarts';
import Chart from '../../../models/Chart';
import Config from './config';
import { isEmpty } from 'utils/object';

class BasicDoubleXChart extends Chart {
  dependency = [];
  config = Config;
  chart: any = null;
  private groupXAxisOffsetHandler?: () => void;

  constructor() {
    super('double-x', 'chartName', 'fsux_tubiao_shuangzhoutu');
    this.meta.requirements = [
      {
        group: 2,
        aggregate: [1, 999],
      },
    ];
  }

  onMount(options, context): void {
    if (options.containerId === undefined || !context.document) {
      return;
    }

    this.chart = init(
      context.document.getElementById(options.containerId),
      'default',
    );
    this.chart.lastBandWidth = 0;
    this.groupXAxisOffsetHandler = () => this.updateGroupXAxisOffset();
    this.chart.on('rendered', this.groupXAxisOffsetHandler);
    this.mouseEvents?.forEach(event => {
      this.chart.on(event.name, event.callback);
    });
  }

  onUpdated(props): void {
    if (!props.dataset || !props.dataset.columns || !props.config) {
      return;
    }
    if (!this.isMatchRequirement(props.config)) {
      return this.chart?.clear();
    }
    const newOptions = this.getOptions(props.dataset, props.config);
    this.chart?.setOption(Object.assign({}, newOptions), true);
    setTimeout(() => {
      this.updateGroupXAxisOffset();
    }, 0);
  }

  onUnMount(): void {
    this.groupXAxisOffsetHandler &&
      this.chart?.off('rendered', this.groupXAxisOffsetHandler);
    this.groupXAxisOffsetHandler = undefined;
    this.chart?.dispose();
  }

  onResize(opt: any, context): void {
    this.chart?.resize(context);
    this.onUpdated(opt);
  }

  private getOptions(dataset: ChartDataSetDTO, config: ChartConfig) {
    const dataConfigs = config.datas || [];
    const styleConfigs = config.styles || [];
    const settingConfigs = config.settings || [];

    const chartDataSet = transformToDataSet(
      dataset.rows,
      dataset.columns,
      dataConfigs,
    );

    const dimension1Configs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.GROUP && c.key === 'dimension1')
      .flatMap(config => config.rows || []);
    const dimension2Configs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.GROUP && c.key === 'dimension2')
      .flatMap(config => config.rows || []);
    const leftMetricConfigs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.AGGREGATE && c.key === 'metricsL')
      .flatMap(config => config.rows || []);
    const rightMetricConfigs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.AGGREGATE && c.key === 'metricsR')
      .flatMap(config => config.rows || []);
    const infoConfigs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.INFO)
      .flatMap(config => config.rows || []);
    const colorConfigs: ChartDataSectionField[] = dataConfigs
      .filter(c => c.type === ChartDataSectionType.COLOR)
      .flatMap(config => config.rows || []);

    const dimension1Config = dimension1Configs[0];
    const dimension2Config = dimension2Configs[0];
    const allMetricConfigs = leftMetricConfigs.concat(rightMetricConfigs);

    if (!dimension1Config || !dimension2Config || !leftMetricConfigs.length) {
      return {};
    }

    if (this.chart) {
      this.chart.lastBandWidth = 0;
    }
    const xAxis = this.getXAxis(
      styleConfigs,
      dimension1Config,
      dimension2Config,
      chartDataSet,
    );
    const yAxisNames = allMetricConfigs.map(getColumnRenderName);
    const series = this.getSeries(
      styleConfigs,
      settingConfigs,
      colorConfigs,
      leftMetricConfigs,
      rightMetricConfigs,
      chartDataSet,
    );

    const option = setOptionsByAxisLabelOverflow({
      chart: this.chart,
      xAxis: xAxis[0] as any,
      yAxis: this.getYAxis(
        styleConfigs,
        leftMetricConfigs,
        rightMetricConfigs,
        chartDataSet,
      ),
      grid: getGridStyle(styleConfigs),
      series,
      yAxisNames,
    });

    let [tooltipFont] = getStyles(styleConfigs, ['label'], ['tooltipFont']);
    let [tooltipBackgroundColor] = getStyles(
      styleConfigs,
      ['label'],
      ['backgroundColor'],
    );

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'none',
        },
        backgroundColor: tooltipBackgroundColor,
        textStyle: {
          ...tooltipFont,
        },
        formatter: this.getTooltipFormmaterFunc(
          [dimension1Config, dimension2Config],
          allMetricConfigs,
          infoConfigs,
          chartDataSet,
        ),
      },
      legend: this.getLegend(styleConfigs, yAxisNames),
      ...option,
      xAxis: [option.xAxis, xAxis[1]],
    };
  }

  private getXAxis(
    styles: ChartStyleConfig[],
    dimension1Config: ChartDataSectionField,
    dimension2Config: ChartDataSectionField,
    chartDataSet: IChartDataSet<string>,
  ): any[] {
    let [
      showAxis,
      inverse,
      lineStyle,
      showLabel,
      font,
      rotate,
      showInterval,
      interval,
      overflow,
      offset,
      formatter,
    ] = getStyles(
      styles,
      ['xAxis'],
      [
        'showAxis',
        'inverseAxis',
        'lineStyle',
        'showLabel',
        'font',
        'rotate',
        'showInterval',
        'interval',
        'overflow',
        'offset',
        'formatter',
      ],
    );
    const [showVerticalLine, verticalLineStyle] = getStyles(
      styles,
      ['splitLine'],
      ['showVerticalLine', 'verticalLineStyle'],
    );
    const xAxis = {
      type: 'category',
      position: 'bottom',
      tooltip: { show: true },
      inverse,
      axisLabel: {
        ...getAxisLabel(
          showLabel,
          font,
          showInterval ? interval : null,
          rotate,
          overflow,
          formatter,
        ),
        margin: 10,
      },
      axisLine: getAxisLine(showAxis, lineStyle),
      axisTick: {
        ...getAxisTick(showLabel, lineStyle),
        length: 20,
      },
      splitLine: getSplitLine(showVerticalLine, verticalLineStyle),
      data: chartDataSet.map(d => d.getCell(dimension2Config)),
      offset,
    };

    return [
      xAxis,
      this.getGroupXAxis(xAxis, dimension1Config, chartDataSet),
    ];
  }

  private getGroupXAxis(
    xAxis,
    dimension1Config: ChartDataSectionField,
    chartDataSet: IChartDataSet<string>,
  ) {
    const groupValues = chartDataSet.map(d =>
      this.toAxisValue(d.getCell(dimension1Config)),
    );
    const { labels, separators } = this.getGroupXAxisLabels(groupValues);

    return {
      ...xAxis,
      data: labels,
      axisTick: {
        ...xAxis.axisTick,
        length: 40,
        interval: index => Boolean(separators[index]),
      },
      axisLabel: {
        ...xAxis.axisLabel,
        interval: 0,
        margin: 30,
        rich: {
          offset: {
            width: 0,
          },
        },
      },
      splitLine: {
        show: false,
      },
    };
  }

  private getGroupXAxisLabels(values: string[]) {
    const labels = values.map(() => '');
    const separators = values.map(() => false);

    let startIndex = 0;
    while (startIndex < values.length) {
      const groupValue = values[startIndex];
      let endIndex = startIndex;

      while (endIndex + 1 < values.length && values[endIndex + 1] === groupValue) {
        endIndex += 1;
      }

      const groupLength = endIndex - startIndex + 1;
      const labelIndex =
        groupLength % 2 === 0
          ? startIndex + groupLength / 2 - 1
          : startIndex + Math.floor(groupLength / 2);

      labels[labelIndex] =
        groupLength % 2 === 0 ? `{offset|}${groupValue}` : groupValue;
      separators[startIndex] = true;
      startIndex = endIndex + 1;
    }

    return { labels, separators };
  }

  private getYAxis(
    styles: ChartStyleConfig[],
    leftMetricConfigs: ChartDataSectionField[],
    rightMetricConfigs: ChartDataSectionField[],
    chartDataSet: IChartDataSet<string>,
  ) {
    const leftYAxis = this.getYAxisByMetrics(
      styles,
      leftMetricConfigs,
      chartDataSet,
      'left',
    );

    if (!rightMetricConfigs.length) {
      return [leftYAxis];
    }

    return [
      leftYAxis,
      this.getYAxisByMetrics(styles, rightMetricConfigs, chartDataSet, 'right'),
    ];
  }

  private getYAxisByMetrics(
    styles: ChartStyleConfig[],
    metricConfigs: ChartDataSectionField[],
    chartDataSet: IChartDataSet<string>,
    position: 'left' | 'right',
  ) {
    const [showHorizonLine, horizonLineStyle] = getStyles(
      styles,
      ['splitLine'],
      ['showHorizonLine', 'horizonLineStyle'],
    );
    let [showAxis, inverse, showName, font, showLabel, max, min] = getStyles(
      styles,
      [`${position}Y`],
      ['showAxis', 'inverseAxis', 'showName', 'font', 'showLabel', 'max', 'min'],
    );

    const yData = metricConfigs
      .flatMap(metricConfig =>
        chartDataSet.flatMap(d =>
          isEmpty(d.getCell(metricConfig)) ? [] : d.getCell(metricConfig),
        ),
      )
      .map(item => parseFloat(item))
      .filter(item => !Number.isNaN(item));

    const dataMin = yData.length ? Math.min(...yData) : undefined;
    const dataMax = yData.length ? Math.max(...yData) : undefined;
    const minValue =
      min !== undefined && min !== null
        ? min
        : dataMin === undefined
        ? undefined
        : dataMin - Math.abs(dataMin) * 0.05;
    const maxValue =
      max !== undefined && max !== null
        ? max
        : dataMax === undefined
        ? undefined
        : dataMax + Math.abs(dataMax) * 0.05;

    return {
      type: 'value',
      position,
      name: showName ? metricConfigs.map(getColumnRenderName).join('/') : '',
      nameLocation: 'middle',
      min: minValue,
      max: maxValue,
      inverse,
      nameTextStyle: {
        color: '#666',
        fontFamily: 'PingFang SC',
        fontSize: 12,
      },
      show: getAxisLine(showAxis).show,
      splitLine: getSplitLine(showHorizonLine, horizonLineStyle),
      axisLine: getAxisLine(showAxis),
      axisLabel: {
        ...getAxisLabel(showLabel, font),
        formatter: function (value) {
          return parseFloat(value.toFixed(2));
        },
      },
    };
  }

  private getSeries(
    styles: ChartStyleConfig[],
    settingConfigs: ChartStyleConfig[],
    colorConfigs: ChartDataSectionField[],
    leftMetricConfigs: ChartDataSectionField[],
    rightMetricConfigs: ChartDataSectionField[],
    chartDataSet: IChartDataSet<string>,
  ) {
    const buildSeries = (
      metricConfig: ChartDataSectionField,
      direction: 'leftY' | 'rightY',
      yAxisIndex: number,
    ) => {
      const [graphType, graphStyle] = getStyles(
        styles,
        [direction],
        ['graphType', 'graphStyle'],
      );
      const type =
        graphType || (direction === 'leftY' ? 'bar' : 'line');

      return {
        name: getColumnRenderName(metricConfig),
        type,
        yAxisIndex,
        sampling: 'average',
        data: chartDataSet.map(dc => ({
          ...metricConfig,
          ...getExtraSeriesRowData(dc),
          ...getExtraSeriesDataFormat(metricConfig?.format),
          value: dc.getCell(metricConfig),
          itemStyle: {
            color: colorConfigs?.[0]?.color?.colors?.find(c => {
              return c.key === getExtraSeriesRowData(dc).rowData[colorConfigs?.[0].colName];
            })?.value,
          },
        })),
        ...this.getItemStyle(metricConfig, styles),
        ...this.getGraphStyle(type, graphStyle),
        ...(type === 'line' ? this.getLineStyle(styles) : {}),
        ...this.getLabelStyle(styles),
        ...getReference2(settingConfigs, chartDataSet, metricConfig, false),
      };
    };

    return leftMetricConfigs
      .map(metricConfig => buildSeries(metricConfig, 'leftY', 0))
      .concat(
        rightMetricConfigs.map(metricConfig =>
          buildSeries(metricConfig, 'rightY', 1),
        ),
      );
  }

  private getGraphStyle(
    graphType: string,
    style,
  ): { lineStyle?: LineStyle; barWidth?: string; color?: string } {
    if (graphType === 'line') {
      return { lineStyle: style };
    }

    return {
      barWidth: style?.width,
      color: style?.color,
    };
  }

  private getLineStyle(styles: ChartStyleConfig[]) {
    const [smooth, step, symbol] = getStyles(
      styles,
      ['graph'],
      ['smooth', 'step', 'symbol'],
    );

    return {
      smooth,
      step,
      symbol: symbol ? 'emptyCircle' : 'none',
    };
  }

  private getItemStyle(config, styles) {
    const color = config?.color?.start;
    const [borderStyle, borderRadius] = getStyles(
      styles,
      ['graph'],
      ['borderStyle', 'radius'],
    );
    return {
      itemStyle: {
        color,
        borderRadius,
        borderType: borderStyle?.type,
        borderWidth: borderStyle?.width,
        borderColor: borderStyle?.color,
      },
    };
  }

  private getLegend(styles, seriesNames): LegendStyle {
    const [show, type, font, legendPos, selectAll, left] = getStyles(
      styles,
      ['legend'],
      ['showLegend', 'type', 'font', 'position', 'selectAll', 'left'],
    );
    let positions = {};
    let orient = '';
    let leftVal = left ? (left === 'left' ? 18 : left) : 8;

    switch (legendPos) {
      case 'top':
        orient = 'horizontal';
        positions = { top: 8, left: leftVal, right: 8, height: 32 };
        break;
      case 'bottom':
        orient = 'horizontal';
        positions = { bottom: 8, left: leftVal, right: 8, height: 32 };
        break;
      case 'left':
        orient = 'vertical';
        positions = { left: 8, top: leftVal, bottom: 24, width: 96 };
        break;
      default:
        orient = 'vertical';
        positions = { right: 8, top: leftVal, bottom: 24, width: 96 };
        break;
    }
    const selected = seriesNames.reduce(
      (obj, name) => ({
        ...obj,
        [name]: selectAll,
      }),
      {},
    );

    return {
      ...positions,
      show,
      type,
      orient,
      selected,
      data: seriesNames,
      textStyle: font,
    };
  }

  private getLabelStyle(styles: ChartStyleConfig[]): LabelStyle {
    const [showLabel] = getStyles(styles, ['graph'], ['label']);

    return {
      label: {
        show: showLabel,
        position: 'top',
        formatter: params => {
          const { value, data } = params;
          return toFormattedValue(value, data.format);
        },
      },
    };
  }

  private getTooltipFormmaterFunc(
    groupConfigs: ChartDataSectionField[],
    aggregateConfigs: ChartDataSectionField[],
    infoConfigs: ChartDataSectionField[],
    chartDataSet: IChartDataSet<string>,
  ): (params) => string {
    return seriesParams => {
      return getSeriesTooltips4Polar2(
        chartDataSet,
        seriesParams[0],
        groupConfigs,
        [],
        aggregateConfigs,
        infoConfigs,
      );
    };
  }

  private toAxisValue(value): string {
    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }

  private updateGroupXAxisOffset() {
    const xAxis = this.chart?.getOption()?.xAxis;
    if (!Array.isArray(xAxis) || xAxis.length < 2) {
      return;
    }

    const bandWidth = this.getRenderedBandWidth();

    if (!bandWidth || bandWidth === this.chart.lastBandWidth) {
      return;
    }

    this.chart.lastBandWidth = bandWidth;
    setTimeout(() => {
      this.setGroupXAxisOffset(this.chart.lastBandWidth);
    }, 0);
  }

  private getRenderedBandWidth(): number | undefined {
    const chartViews = this.chart?._chartsViews || [];
    const chartView = chartViews.find(view => {
      const bandWidth =
        view?.renderTask?.context?.outputData?._layout?.bandWidth;
      return typeof bandWidth === 'number' && !Number.isNaN(bandWidth);
    });

    return chartView?.renderTask?.context?.outputData?._layout?.bandWidth;
  }

  private setGroupXAxisOffset(width: number) {
    const optionNew = {
      xAxis: [
        {},
        {
          axisLabel: {
            rich: {
              offset: {
                width,
              },
            },
          },
        },
      ],
    };
    this.chart?.setOption(optionNew);
  }
}

export default BasicDoubleXChart;
